import React, { useState, useEffect, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, Filter, Globe, Star, ChevronDown, Briefcase,
  Languages, Clock, Shield, CheckCircle, MessageSquare,
  Heart, User, Mail, X
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Card } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/Button";
import FavoriteButton from "../../components/ui/FavoriteButton";
import SharedFooter3 from "../../Footer/SharedFooter3";
import SharedHeader2 from "../../Headers/SharedHeader2";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { getInternationalProfessionals } from "../../redux/Actions/AdminAction";
import { getTypeCategories } from "../../redux/Actions/ClientActions";
import { getAllServiceAreas } from "../../redux/Actions/ServiceAreaAction";
import { selectLoaderValue } from "../../redux/Slices/loadingSlice";
import { useMultipleApiLoading } from "../../hooks/useMultipleApiLoading";
import { getTranslatedCategoryName } from "../../utils/categoryTranslation";
import { TypeEnum } from "../../modules/shared/model";
import MyProfile from "../../modules/professional/components/MyProfile";
import SharedHeader5 from "../../Headers/SharedHeader5";
import RequestQuoteModal from "../../components/ui/RequestQuoteModal";
import { Offer } from "../../components/ui/RequestQuoteModal";
import SuccessNotification from "../../components/ui/SuccessNotification";
import ReviewModal from "../../components/ui/ReviewModal";
import { getDynamicCurrencySymbol } from "../../lib/currencyUtils";

// RealTimeFavoriteButton component for international professionals
interface RealTimeFavoriteButtonProps {
  professional: any;
  currentUserFavorite: any;
}

const RealTimeFavoriteButton = React.memo(({ professional, currentUserFavorite }: RealTimeFavoriteButtonProps) => {
  const [isFavorite, setIsFavorite] = useState(professional.favorite?.id ? true : false);

  // Listen for changes in currentUserFavorite to update the button state
  useEffect(() => {
    if (currentUserFavorite && currentUserFavorite.userIds) {
      const isFavorited = currentUserFavorite.userIds.some(user => user.id === professional.id.toString());
      setIsFavorite(isFavorited);
    } else if (currentUserFavorite === null) {
      // If no favorites exist, set to false
      setIsFavorite(false);
    }
  }, [currentUserFavorite, professional.id]);

  return (
    <FavoriteButton
      targetUserId={professional.id.toString()}
      size="sm"
      favoriteData={{
        id: professional.favorite?.id,
        isFavorite: isFavorite
      }}
    />
  );
});

RealTimeFavoriteButton.displayName = "RealTimeFavoriteButton";

const InternationalProfessionalsProfile = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language.split("-")[0];
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [hourlyRange, setHourlyRange] = useState([0, 150]);
  const [rating, setRating] = useState(0);
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState("USD");
  const [timeZone, setTimeZone] = useState("UTC-5");
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedProfessional, setSelectedProfessional] = useState(null);
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [categoriesToShow, setCategoriesToShow] = useState(8);
  const [showEmergencyOnly, setShowEmergencyOnly] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [visibleSubCounts, setVisibleSubCounts] = useState<Record<string, number>>({});
  const [selectedCountryId, setSelectedCountryId] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [showRequestQuoteModal, setShowRequestQuoteModal] = useState(false);
  const [isContact, setIsContact] = useState(false);
  const [selectedProfessionalForQuote, setSelectedProfessionalForQuote] = useState(null);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const { user } = useAppSelector((state: any) => state.auth);
  const [editingOfferId, setEditingOfferId] = useState<string | undefined>(undefined);
  const [editingOffer, setEditingOffer] = useState<any>(null);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector(selectLoaderValue);
  const { data: professionals, totalCount, hasNextPage, currentPage: storeCurrentPage, totalPages } = useAppSelector((state: any) => state.admin.internationalProfessionals);
  const { typeCategories } = useAppSelector((state: any) => state.client);
  const { currentUserFavorite } = useAppSelector((state: any) => state.favorites);

  // Define the API tasks that need to complete before showing content
  const apiTasks = ["internationalProfessionals", "typeCategories"];

  // Use the multiple API loading hook
  const {
    isAnyLoading: isInitialLoading,
    isAllCompleted,
    startTask,
    completeTask
  } = useMultipleApiLoading({
    taskIds: apiTasks,
    onAllCompleted: () => {
      // All initial APIs completed
    },
    resetOnMount: true
  });

  // Transform categories data for UI
  const categories = useMemo(() => {
    if (!typeCategories || !Array.isArray(typeCategories)) {
      return ["All Categories"];
    }

    return [
      "All Categories",
      ...typeCategories.map((category: any) => ({
        name: category.name || "Unknown Category",
        id: category.id || ""
      }))
    ];
  }, [typeCategories]);

  // Get categories to display (first N + "All Categories")
  const displayedCategories = useMemo(() => {
    return categories.slice(0, categoriesToShow);
  }, [categories, categoriesToShow]);

  // Check if there are more categories to show
  const hasMoreCategories = categories.length > categoriesToShow;

  // Handle showing more categories
  const handleShowMoreCategories = () => {
    setCategoriesToShow(prev => Math.min(prev + 8, categories.length));
  };

  // Handle showing fewer categories
  const handleShowLessCategories = () => {
    setCategoriesToShow(8);
  };

  // Fetch international professionals on component mount
  useEffect(() => {
    // Only fetch professionals if user is available
    if (!user?.id) {
      completeTask("internationalProfessionals");
      return;
    }

    startTask("internationalProfessionals");
    dispatch(getInternationalProfessionals({ page: 1, limit: 10, jobUserId: user.id })).then(() => {
      completeTask("internationalProfessionals");
    }).catch(() => {
      completeTask("internationalProfessionals");
    });
  }, [dispatch, user?.id]);

  // Fetch categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        startTask("typeCategories");
        // Fetch categories for both Home Service and Professional Service
        await dispatch(getTypeCategories({ id: TypeEnum.International_Service }));
        completeTask("typeCategories");
      } catch (error) {
        console.error("Error fetching categories:", error);
        completeTask("typeCategories");
      }
    };
    fetchCategories();
  }, [dispatch]);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500); // 500ms delay

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Effect to trigger search when debounced query changes
  useEffect(() => {
    if (debouncedSearchQuery !== undefined && user?.id) {
      dispatch(getInternationalProfessionals({
        page: 1,
        limit: 10,
        jobUserId: user.id,
        search: debouncedSearchQuery,
        category: selectedCategory !== "all" ? selectedCategory : undefined,
        categoryId: selectedCategoryId,
        countryId: selectedCountryId,
        minHourlyRate: hourlyRange[0] > 0 ? hourlyRange[0] : undefined,
        maxHourlyRate: hourlyRange[1] > 0 ? hourlyRange[1] : undefined,
        isEmergency: showEmergencyOnly ? true : undefined
      }));
    }
  }, [debouncedSearchQuery, selectedCategory, selectedCategoryId, selectedCountryId, hourlyRange, showEmergencyOnly, dispatch, user?.id]);

  // Effect to trigger API call when categoryId changes
  useEffect(() => {
    if (!user?.id) {
      completeTask("internationalProfessionals");
      return;
    }

    startTask("internationalProfessionals");
    dispatch(getInternationalProfessionals({
      page: 1,
      limit: 10,
      jobUserId: user.id,
      search: debouncedSearchQuery,
      category: selectedCategory !== "all" ? selectedCategory : undefined,
      categoryId: selectedCategoryId,
      countryId: selectedCountryId,
      minHourlyRate: hourlyRange[0] > 0 ? hourlyRange[0] : undefined,
      maxHourlyRate: hourlyRange[1] > 0 ? hourlyRange[1] : undefined,
      isEmergency: showEmergencyOnly ? true : undefined
    })).then(() => {
      completeTask("internationalProfessionals");
    }).catch(() => {
      completeTask("internationalProfessionals");
    });
  }, [selectedCategoryId, dispatch, user?.id]);

  // Handle search (now automatic with debouncing)
  const handleSearch = () => {
    if (!user?.id) return;

    // Trigger immediate search without debouncing
    dispatch(getInternationalProfessionals({
      page: 1,
      limit: 10,
      jobUserId: user.id,
      search: searchQuery,
      category: selectedCategory !== "all" ? selectedCategory : undefined,
      categoryId: selectedCategoryId,
      countryId: selectedCountryId,
      minHourlyRate: hourlyRange[0] > 0 ? hourlyRange[0] : undefined,
      maxHourlyRate: hourlyRange[1] > 0 ? hourlyRange[1] : undefined,
      isEmergency: showEmergencyOnly ? true : undefined
    }));
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    if (!user?.id) return;

    dispatch(getInternationalProfessionals({
      page,
      limit: 10,
      jobUserId: user.id,
      search: debouncedSearchQuery,
      category: selectedCategory !== "all" ? selectedCategory : undefined,
      categoryId: selectedCategoryId,
      countryId: selectedCountryId,
      minHourlyRate: hourlyRange[0] > 0 ? hourlyRange[0] : undefined,
      maxHourlyRate: hourlyRange[1] > 0 ? hourlyRange[1] : undefined,
      isEmergency: showEmergencyOnly ? true : undefined
    }));
  };

  interface ProfessionalCardProps {
    professional: any;
  }

  const ProfessionalCard = React.memo(({ professional }: ProfessionalCardProps) => {
    const getOnlineStatusColor = (status) => {
      return status === "online" ? "bg-green-500" : "bg-yellow-500";
    };

    return (
      <Card className="p-4 sm:p-6 hover:shadow-lg transition-shadow">
        <div className="flex flex-col sm:flex-row items-start gap-4" style={{ textAlign: "left" }}>
          {/* Profile Image & Online Status */}
          <div className="flex-shrink-0 relative w-full sm:w-auto flex justify-center sm:block">
            {professional.photo?.path ? (
              <div className="w-20 h-20 sm:w-16 sm:h-16 rounded-full overflow-hidden">
                <img
                  src={professional.photo.path}
                  alt={`${professional.name}'s profile`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    // Fallback to default avatar if image fails to load
                    const target = e.target as HTMLImageElement;
                    target.style.display = "none";
                    const fallback = target.nextSibling as HTMLElement;
                    if (fallback) {
                      fallback.style.display = "flex";
                    }
                  }}
                />
                <div className="w-full h-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center" style={{ display: "none" }}>
                  <User className="w-10 h-10 sm:w-8 sm:h-8 text-blue-600" />
                </div>
              </div>
            ) : (
              <div className="w-20 h-20 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
                <User className="w-10 h-10 sm:w-8 sm:h-8 text-blue-600" />
              </div>
            )}
            <div className={`absolute bottom-0 right-1/2 sm:right-0 translate-x-8 sm:translate-x-0 w-4 h-4 rounded-full border-2 border-white ${getOnlineStatusColor(professional.onlineStatus)}`} />
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0 w-full" style={{ textAlign: "left" }}>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-0 mb-2">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 truncate">
                  {professional.title}
                </h3>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Globe className="w-4 h-4" />
                  <span>{professional.country}</span>
                  <span className="text-gray-400 hidden sm:inline">•</span>
                  <span>{professional.timeZone}</span>
                </div>
              </div>
              <div className="flex flex-end gap-2 items-center">
                <RealTimeFavoriteButton professional={professional} currentUserFavorite={currentUserFavorite} />
                <Button
                  variant="outline"
                  className="w-full flex items-center justify-center gap-2"
                  onClick={() => {
                    setSelectedProfessional(professional);
                    setShowProfileDialog(true);
                  }}
                >
                  {t("internationalProfessionals.viewProfile")}
                </Button>
              </div>
            </div>

            <p className="text-sm text-gray-600 mb-2">{professional.title}</p>

            <div className="flex flex-wrap items-center gap-2 mb-3">
              <div className="flex items-center">
                <Star className="w-4 h-4 text-yellow-400 fill-current" />
                <span className="ml-1 text-sm font-medium">{professional.rating}</span>
              </div>
              <button
                onClick={() => {
                  console.log("Professional object:", professional);
                  setSelectedProfessional(professional);
                  setShowReviewModal(true);
                }}
              className="text-sm text-blue-600 hover:text-blue-700 hover:underline"
            >
              {professional.reviewsCount || 0} {t("internationalProfessionals.reviews")}
            </button>
            <span className="text-gray-400 hidden sm:inline">•</span>
            <span className="text-sm text-gray-600">{professional.completedJobs} {t("internationalProfessionals.jobs")}</span>

            {/* Emergency Badge */}
            {professional.isEmergency && (
              <Badge variant={undefined} className="bg-red-100 text-red-700 border-red-200 flex items-center gap-1">
                <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                {t("internationalProfessionals.emergency")}
              </Badge>
            )}
            </div>

            <p className="text-sm text-gray-600 mb-3 line-clamp-2 sm:line-clamp-none">
              {professional.description}
            </p>

            <div className="flex flex-wrap gap-2 mb-3">
              {professional.skills.map((skill, index) => (
                <Badge
                  key={index}
                  variant="outline"
                  className="bg-blue-50 text-blue-700 border-blue-200 text-xs whitespace-nowrap"
                >
                  {skill}
                </Badge>
              ))}
            </div>

            {/* Categories and Subcategories */}
            {professional.categories && professional.categories.length > 0 && (
              <div className="mb-3">
                <h4 className="text-sm font-medium text-gray-700 mb-2">{t("internationalProfessionals.categoriesSpecializations")}</h4>
                <div className="flex flex-wrap gap-2">
                  {professional.categories.map((category, index) => {
                    const translatedName = getTranslatedCategoryName(category, currentLang);
                    const rawSubcategories = category.translatedSubCategories?.[currentLang] ?? category.subcategories ?? [];
                    const normalizedSubcategories = Array.isArray(rawSubcategories)
                      ? rawSubcategories
                      : typeof rawSubcategories === "string"
                        ? rawSubcategories
                          .split(",")
                          .map((item) => item.trim())
                          .filter(Boolean)
                        : [];
                    const hasSubcategories = normalizedSubcategories.length > 0;
                    return (
                    <div key={index} className="flex flex-col gap-1">
                      <Badge
                        variant="outline"
                        className={`text-xs ${category.type?.id === "2"
                          ? "bg-red-50 text-red-700 border-red-200"
                          : "bg-green-50 text-green-700 border-green-200"
                          }`}
                      >
                        {translatedName}
                      </Badge>
                      {hasSubcategories && (
                        <div className="flex flex-col gap-1 ml-2">
                          <div className="flex flex-wrap gap-1">
                            {(normalizedSubcategories.slice(0, visibleSubCounts[`${professional.id}-${index}`] ?? 3)).map((subcategory, subIndex) => (
                              <Badge
                                key={subIndex}
                                variant="outline"
                                className="bg-gray-50 text-gray-600 border-gray-200 text-xs"
                              >
                                {subcategory}
                              </Badge>
                            ))}
                          </div>
                          {normalizedSubcategories.length > (visibleSubCounts[`${professional.id}-${index}`] ?? 3) && (
                            <button
                              onClick={() => setVisibleSubCounts(prev => ({
                                ...prev,
                                [`${professional.id}-${index}`]: (prev[`${professional.id}-${index}`] ?? 3) + 3
                              }))}
                              className="text-xs text-blue-600 hover:text-blue-700 hover:underline self-start"
                            >
                              {t("internationalProfessionals.showMore")}
                            </button>
                          )}
                          {(visibleSubCounts[`${professional.id}-${index}`] ?? 3) > 3 && (
                            <button
                              onClick={() => setVisibleSubCounts(prev => ({
                                ...prev,
                                [`${professional.id}-${index}`]: 3
                              }))}
                              className="text-xs text-blue-600 hover:text-blue-700 hover:underline self-start ml-2"
                            >
                              {t("internationalProfessionals.seeLess")}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );})}
                </div>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
              <div className="flex items-center">
                <Clock className="w-4 h-4 mr-1" />
                {professional.responseTime}
              </div>
              <div className="flex items-center">
                <Languages className="w-4 h-4 mr-1" />
                {professional.languages.join(", ")}
              </div>
            </div>

            <div className="mt-4 pt-4 border-t flex flex-col sm:flex-row sm:items-center gap-4 sm:justify-between">
              <div className="flex flex-wrap items-center gap-2">
                {professional.isEmergency && (
                  <Badge variant={undefined} className="bg-red-100 text-red-700 border-red-200 flex items-center gap-1">
                    <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                    {t("internationalProfessionals.emergencyService")}
                  </Badge>
                )}
                {professional.verified && (
                  <Badge variant={undefined} className="bg-green-50 text-green-700 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" />
                    {t("internationalProfessionals.verified")}
                  </Badge>
                )}
                <Badge variant={undefined} className="bg-blue-50 text-blue-700">
                  {professional.completedJobs}+ {t("internationalProfessionals.jobs")}
                </Badge>
              </div>
              <div className="flex items-center order-first sm:order-last">
                <span className="text-lg font-semibold text-gray-900">
                  {getDynamicCurrencySymbol()}{professional.hourlyRate}
                </span>
                <span className="text-sm text-gray-500">/hr</span>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Button
                onClick={() => {
                  // const hasOffers = Array.isArray(professional.offers) && professional.offers.length > 0;
                  // if (hasOffers) {
                  //   const firstOffer = professional.offers[0];
                  //   navigate(`/client/MessageAndVideoCall?professionalId=${professional.id}&professionalName=${encodeURIComponent(professional.name)}${firstOffer?.job?.id ? `&offerId=${firstOffer.id}` : ""}`);
                  // } else {
                  setSelectedProfessionalForQuote(professional);
                  setIsContact(true);
                  setEditingOfferId(undefined);
                  setEditingOffer(null);
                  setShowRequestQuoteModal(true);
                  // }
                }}
                className="w-full bg-blue-600 text-white hover:bg-blue-700 flex items-center justify-center gap-2"
              >
                <MessageSquare className="w-4 h-4" />
                {t("internationalProfessionals.contact")}
              </Button>
              {/* <Button
                variant="outline"
                className="w-full flex items-center justify-center gap-2"
                onClick={() => {
                  // const hasOffers = Array.isArray(professional.offers) && professional.offers.length > 0;
                  setSelectedProfessionalForQuote(professional);
                  // if (hasOffers) {
                  //   const firstOffer = professional.offers[0];
                  //   setEditingOfferId(firstOffer?.id);
                  //   setEditingOffer(firstOffer || null);
                  // } else {
                  //   setEditingOfferId(undefined);
                  //   setEditingOffer(null);
                  // }
                  setEditingOfferId(undefined);
                  setEditingOffer(null);
                  setShowRequestQuoteModal(true);
                }}
              >
                <Mail className="w-4 h-4" />
                Request Quote
              </Button> */}
            </div>
          </div>
        </div>
      </Card>
    );
  });

  ProfessionalCard.displayName = "ProfessionalCard";

  interface FiltersPanelProps {
    isMobile?: boolean;
  }

  const FiltersPanel = React.memo(({ isMobile = false }: FiltersPanelProps) => (
    <div className="space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto pr-2">
      <div>
        <h3 className="text-sm font-medium text-gray-900 mb-3">{t("internationalProfessionals.categories")}</h3>
        <div className="space-y-2">
          {displayedCategories.map((category) => {
            const categoryName = typeof category === "string" ? category : category.name;
            const categoryId = typeof category === "string" ? "" : category.id;
            return (
              <label key={categoryName} className="flex items-center">
                <input
                  type="radio"
                  name="category"
                  checked={selectedCategory === categoryName}
                  onChange={() => {
                    setSelectedCategory(categoryName);
                    setSelectedCategoryId(categoryId);
                    if (isMobile) setShowMobileFilters(false);
                  }}
                  className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-600">{categoryName}</span>
              </label>
            );
          })}

          {/* Show More/See Less Buttons */}
          {hasMoreCategories && (
            <button
              onClick={handleShowMoreCategories}
              className="w-full py-2 text-sm text-blue-600 hover:text-blue-700 hover:underline mt-2"
            >
              {t("internationalProfessionals.showMore")} ({categories.length - categoriesToShow} more)
            </button>
          )}

          {categoriesToShow > 8 && (
            <button
              onClick={handleShowLessCategories}
              className="w-full py-2 text-sm text-gray-600 hover:text-gray-700 hover:underline mt-1"
            >
              {t("internationalProfessionals.seeLess")}
            </button>
          )}
        </div>
      </div>

      <Button
        variant="ghost"
        className="w-full text-blue-600 hover:text-blue-700 hover:bg-blue-50"
        onClick={() => {
          setSelectedCategory("all");
          setSelectedCategoryId("");
          setSearchQuery("");
          setDebouncedSearchQuery("");
          setHourlyRange([0, 150]);
          setRating(0);
          setSelectedLanguages([]);
          setTimeZone("UTC-5");
          setCategoriesToShow(8);
          setShowEmergencyOnly(false);
          setExpandedCategories({});
          setVisibleSubCounts({});
          setSelectedCountryId("");
          if (isMobile) setShowMobileFilters(false);
        }}
      >
        {t("internationalProfessionals.resetFilters")}
      </Button>
    </div>
  ));

  FiltersPanel.displayName = "FiltersPanel";

  return (
    <div className="min-h-screen bg-gray-50" style={{ textAlign: "left" }}>
      <SharedHeader5 />

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <div className="relative">
            <input
              type="text"
              placeholder={t("internationalProfessionals.searchInternationalProfessionals")}
              className="w-full pl-12 pr-20 py-3 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleSearch();
                }
              }}
            />
            <Search className="absolute left-4 top-3.5 text-gray-400 w-5 h-5" />

            {/* Loading indicator */}
            {isLoading && (
              <div className="absolute right-16 top-3.5">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
              </div>
            )}

            {/* Search button */}
            <button
              onClick={handleSearch}
              disabled={!user?.id}
              className={`absolute right-2 top-2 px-3 py-1.5 rounded-md transition-colors text-sm ${!user?.id
                  ? "bg-gray-400 text-gray-200 cursor-not-allowed"
                  : "bg-blue-600 text-white hover:bg-blue-700"
                }`}
            >
              {!user?.id ? t("internationalProfessionals.pleaseLogInToSearch") : t("internationalProfessionals.search")}
            </button>
          </div>

          {/* Search status */}
          {searchQuery && (
            <div className="mt-2 text-sm text-gray-600">
              {isLoading ? t("internationalProfessionals.searching") : t("internationalProfessionals.searchingFor", { query: searchQuery })}
            </div>
          )}
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          <div className="hidden lg:block w-64 flex-shrink-0">
            <Card className="p-6 sticky top-24">
              <FiltersPanel />
            </Card>
          </div>

          <AnimatePresence>
            {showMobileFilters && (
              <>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 bg-black bg-opacity-50 z-50 lg:hidden"
                  onClick={() => setShowMobileFilters(false)}
                />
                <motion.div
                  initial={{ x: "100%" }}
                  animate={{ x: 0 }}
                  exit={{ x: "100%" }}
                  transition={{ type: "spring", bounce: 0, duration: 0.4 }}
                  className="fixed right-0 top-0 h-full w-full max-w-xs bg-white z-50 lg:hidden overflow-y-auto"
                >
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-lg font-semibold">{t("internationalProfessionals.filters")}</h2>
                      <button
                        onClick={() => setShowMobileFilters(false)}
                        className="p-2 hover:bg-gray-100 rounded-lg"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                    <FiltersPanel isMobile={true} />
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>

          <div className="flex-1" style={{ textAlign: "left" }}>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setShowMobileFilters(true)}
                  className="lg:hidden p-2 hover:bg-gray-100 rounded-lg"
                >
                  <Filter className="w-5 h-5 text-gray-600" />
                </button>
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
                  {isInitialLoading ? t("internationalProfessionals.loading") : t("internationalProfessionals.internationalProfessionalsFound", { count: totalCount || 0 })}
                </h2>
              </div>
              <div className="flex items-center">
                <select
                  className="px-2 sm:px-3 py-2 border rounded-lg text-xs sm:text-sm text-gray-600 focus:ring-2 focus:ring-blue-500"
                  defaultValue="recommended"
                >
                  <option value="recommended">{t("internationalProfessionals.recommended")}</option>
                  <option value="rating">{t("internationalProfessionals.highestRating")}</option>
                  <option value="price_low">{t("internationalProfessionals.lowestPrice")}</option>
                  <option value="price_high">{t("internationalProfessionals.highestPrice")}</option>
                  <option value="jobs">{t("internationalProfessionals.mostJobs")}</option>
                </select>
              </div>
            </div>

            {/* Professionals Grid */}
            <div className="space-y-4 sm:space-y-6">
              {!user?.id ? (
                <div className="text-center py-8">
                  <p className="text-gray-600">{t("internationalProfessionals.pleaseLogInToViewInternational")}</p>
                </div>
              ) : isInitialLoading ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <p className="mt-2 text-gray-600">{t("internationalProfessionals.loadingProfessionals")}</p>
                </div>
              ) : professionals && professionals.length > 0 ? (
                professionals.map((professional) => (
                  <ProfessionalCard key={professional.id} professional={professional} />
                ))
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-600">{t("internationalProfessionals.noInternationalProfessionalsFound")}</p>
                </div>
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-8 flex justify-center gap-2">
                {/* Previous Page Button */}
                <button
                  onClick={() => handlePageChange(storeCurrentPage - 1)}
                  disabled={storeCurrentPage === 1}
                  className={`px-3 sm:px-4 py-2 rounded-lg text-sm ${storeCurrentPage === 1
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                    : "bg-white text-gray-600 hover:bg-gray-50"
                    }`}
                >
                  {t("internationalProfessionals.previous")}
                </button>

                {/* Page Numbers */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (storeCurrentPage <= 3) {
                    pageNum = i + 1;
                  } else if (storeCurrentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = storeCurrentPage - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`px-3 sm:px-4 py-2 rounded-lg text-sm ${pageNum === storeCurrentPage
                        ? "bg-blue-600 text-white"
                        : "bg-white text-gray-600 hover:bg-gray-50"
                        }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                {/* Next Page Button */}
                <button
                  onClick={() => handlePageChange(storeCurrentPage + 1)}
                  disabled={storeCurrentPage === totalPages}
                  className={`px-3 sm:px-4 py-2 rounded-lg text-sm ${storeCurrentPage === totalPages
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                    : "bg-white text-gray-600 hover:bg-gray-50"
                    }`}
                >
                  {t("internationalProfessionals.next")}
                </button>
              </div>
            )}
          </div>
        </div>

        <AnimatePresence>
          {showReviewModal && selectedProfessional && (
            <ReviewModal
              isOpen={showReviewModal}
              onClose={() => setShowReviewModal(false)}
              professional={selectedProfessional}
            />
          )}
        </AnimatePresence>
      </main>

      {/* Success Notification */}
      <SuccessNotification
        isVisible={showSuccessNotification}
        message={successMessage}
        onClose={() => setShowSuccessNotification(false)}
      />

      {/* Request Quote Modal */}
      <RequestQuoteModal
        isOpen={showRequestQuoteModal}
        onClose={() => {
          setShowRequestQuoteModal(false);
          setSelectedProfessionalForQuote(null);
          setEditingOfferId(undefined);
          setEditingOffer(null);
        }}
        professional={selectedProfessionalForQuote}
        offerId={editingOfferId}
        existingOffer={editingOffer}
        onOfferCreated={(offer: Offer) => {
          setSuccessMessage("The professional receives the quote and accepts it and must chat with the client (currently, the professional who tries to chat cannot find the client's chat and must wait for the client to chat first)");
          setShowSuccessNotification(true);
          if (isContact) {
            navigate(`/client/MessageAndVideoCall?professionalId=${selectedProfessionalForQuote.id}&professionalName=${encodeURIComponent(selectedProfessionalForQuote.name)}&offerId=${offer.id}`);
          }
        }}
        onOfferUpdated={(offer: Offer) => {
          setSuccessMessage("Quote request updated successfully!");
          setShowSuccessNotification(true);
        }}
      />

      {/* Profile Dialog */}
      <AnimatePresence>
        {showProfileDialog && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 z-50"
              onClick={() => setShowProfileDialog(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="fixed inset-4 z-50 overflow-hidden"
            >
              <div className="h-full bg-white rounded-lg shadow-xl flex flex-col">
                {/* Dialog Header */}
                <div className="flex items-center justify-between p-4 border-b">
                  <h2 className="text-lg font-semibold text-gray-900">
                    {t("internationalProfessionals.profileTitle", { name: selectedProfessional?.name })}
                  </h2>
                  <button
                    onClick={() => setShowProfileDialog(false)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>

                {/* Dialog Content */}
                <div className="flex-1 overflow-y-auto p-4">
                  {selectedProfessional && (
                    <MyProfile
                      isAdminEdit={true}
                      isViewing={true}
                      adminUserId={selectedProfessional.id}
                      onUserUpdated={() => {
                        // Refresh the professionals list if needed
                        handleSearch();
                      }}
                    />
                  )}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <br></br>
      <br></br>
      <br></br>
      <br></br>
      <SharedFooter3 />
    </div>
  );
};

export default InternationalProfessionalsProfile;